/**
 * API Client Service
 * Wrapper around fetch for backend API calls with error handling
 */

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api/v0';

/**
 * Handle API response and errors
 * @param {Response} response - Fetch response object
 * @returns {Promise<any>} Parsed JSON response
 * @throws {Error} If response is not ok
 */
async function handleResponse(response) {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({
      message: `HTTP ${response.status}: ${response.statusText}`,
    }));
    throw new Error(errorData.message || errorData.detail || 'API request failed');
  }
  return response.json();
}

/**
 * Make an API request
 * @param {string} endpoint - API endpoint (without base URL)
 * @param {RequestInit} options - Fetch options
 * @returns {Promise<any>} Parsed JSON response
 */
async function apiRequest(endpoint, options = {}) {
  const url = `${API_BASE_URL}${endpoint}`;
  const config = {
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    ...options,
  };

  try {
    const response = await fetch(url, config);
    return await handleResponse(response);
  } catch (error) {
    // Re-throw with more context
    if (error instanceof TypeError && error.message.includes('fetch')) {
      throw new Error('Network error: Unable to connect to server');
    }
    throw error;
  }
}

/**
 * API Client object with common HTTP methods
 */
export const api = {
  /**
   * GET request
   * @param {string} endpoint - API endpoint
   * @param {RequestInit} options - Fetch options
   * @returns {Promise<any>}
   */
  get(endpoint, options = {}) {
    return apiRequest(endpoint, { ...options, method: 'GET' });
  },

  /**
   * POST request
   * @param {string} endpoint - API endpoint
   * @param {any} data - Request body data
   * @param {RequestInit} options - Fetch options
   * @returns {Promise<any>}
   */
  post(endpoint, data, options = {}) {
    return apiRequest(endpoint, {
      ...options,
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * PUT request
   * @param {string} endpoint - API endpoint
   * @param {any} data - Request body data
   * @param {RequestInit} options - Fetch options
   * @returns {Promise<any>}
   */
  put(endpoint, data, options = {}) {
    return apiRequest(endpoint, {
      ...options,
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  /**
   * DELETE request
   * @param {string} endpoint - API endpoint
   * @param {RequestInit} options - Fetch options
   * @returns {Promise<any>}
   */
  delete(endpoint, options = {}) {
    return apiRequest(endpoint, { ...options, method: 'DELETE' });
  },

  /**
   * Upload file(s) with FormData and progress tracking
   * @param {string} endpoint - API endpoint
   * @param {FormData} formData - FormData with files
   * @param {RequestInit} options - Fetch options with optional onUploadProgress callback
   * @returns {Promise<any>}
   */
  upload(endpoint, formData, options = {}) {
    const url = `${API_BASE_URL}${endpoint}`;
    
    // Use XMLHttpRequest for upload progress tracking
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      
      // Set up progress tracking
      if (options.onUploadProgress) {
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            options.onUploadProgress(e);
          }
        });
      }
      
      // Handle response
      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const response = JSON.parse(xhr.responseText);
            resolve(response);
          } catch (e) {
            reject(new Error('Invalid JSON response'));
          }
        } else {
          try {
            const errorData = JSON.parse(xhr.responseText);
            reject(new Error(errorData.message || errorData.detail || `HTTP ${xhr.status}: ${xhr.statusText}`));
          } catch (e) {
            reject(new Error(`HTTP ${xhr.status}: ${xhr.statusText}`));
          }
        }
      });
      
      xhr.addEventListener('error', () => {
        reject(new Error('Network error: Unable to connect to server'));
      });
      
      xhr.addEventListener('abort', () => {
        reject(new Error('Upload aborted'));
      });
      
      // Open and send request
      xhr.open('POST', url);
      
      // Set headers (excluding Content-Type for FormData)
      if (options.headers) {
        Object.keys(options.headers).forEach(key => {
          if (key.toLowerCase() !== 'content-type') {
            xhr.setRequestHeader(key, options.headers[key]);
          }
        });
      }
      
      xhr.send(formData);
    });
  },
};

/**
 * Health check endpoint
 * @returns {Promise<{status: string}>}
 */
export async function checkHealth() {
  // Health endpoint is at root, not under /api/v0
  const response = await fetch('/health');
  return response.json();
}

